import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Markdown } from "@vd/ui";
import { useState } from "react";
import { ApiError, errorMessage } from "../../../../api/client";
import { companiesQuery, ops, qk, reportQuery } from "../../../../api/ops";
import type { BugReportDetail, BugResolution, BugStructuredReport } from "../../../../api/types";
import { Crumbs } from "../../../../components/Crumbs";
import { toast } from "../../../../components/toast";
import { Badge, Button, Card, EmptyState, ErrorState, Loading } from "../../../../components/ui";
import { level } from "../../../../lib/crumbs";
import { fmtDateTime } from "../../../../lib/format";
import {
  EmailBadge,
  isResolved,
  KindBadge,
  reportCode,
  SourceBadge,
  StatusBadge,
  statusLabel,
} from "../-badges";
import { FixProposal } from "../-fix-proposal";
import { ResolveDialog } from "../-resolve";

/** "R-12", once the report loads. */
function useReportName(companyId: string, reportId: string): string | null {
  const r = useQuery(reportQuery(companyId, reportId)).data;
  if (!r) return null;
  return r.number ? reportCode(r.number) : "Report";
}

export const Route = createFileRoute("/_app/reports/$companyId/$reportId")({
  staticData: {
    crumb: {
      label: "Report",
      useLabel: ({ params }) => useReportName(params.companyId ?? "", params.reportId ?? ""),
      parents: [level("Reports", "/reports")],
    },
  },
  loader: ({ context, params }) =>
    context.queryClient.prefetchQuery(reportQuery(params.companyId, params.reportId)),
  component: ReportPage,
});

function ReportPage() {
  const { companyId, reportId } = Route.useParams();
  const q = useQuery(reportQuery(companyId, reportId));
  if (q.isPending) return <Loading />;
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <>
        <Crumbs className="mb-2" />
        {notFound ? (
          <EmptyState title="This report doesn't exist" />
        ) : (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        )}
      </>
    );
  }
  return <ReportView r={q.data} companyId={companyId} />;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v ? v : null;
}

function ReportView({ r, companyId }: { r: BugReportDetail; companyId: string }) {
  const company = useQuery(companiesQuery).data?.items.find((c) => c.id === companyId);
  const d = r.diagnostics as Record<string, Record<string, unknown> | undefined>;
  const facts: [string, string][] = [
    ["Company", company?.name ?? ""],
    ["Sent", fmtDateTime(r.created_at)],
    ["User", r.user ? `${r.user.name}${r.user.email ? ` (${r.user.email})` : ""}` : ""],
    ["App version", r.app_version ?? ""],
    ["OS", r.os ?? ""],
    ["Phone", str(d.phone?.status) ?? ""],
    ["Realtime", str(d.realtime?.status) ?? ""],
    ["Microphone", str(d.audio?.mic_label) ?? ""],
  ];
  const events = Array.isArray(r.diagnostics.events) ? r.diagnostics.events.length : 0;

  return (
    <>
      <Crumbs className="mb-2" />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="font-display font-medium text-display">
          {r.number ? <span className="text-ink-3">{reportCode(r.number)} </span> : null}
          {r.report?.title || `Problem report from ${r.user?.name ?? "an unknown user"}`}
        </h1>
        <span className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={r.status} fixedVersion={r.fixed_version} />
          {r.source === "assistant" ? (
            <>
              <SourceBadge severity={r.report?.severity ?? null} kind={r.report?.kind ?? null} />
              <KindBadge kind={r.report?.kind} />
            </>
          ) : null}
          <EmailBadge status={r.email_status} />
        </span>
      </div>

      <dl className="mb-4 grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg bg-surface-1 px-4 py-3 shadow-material sm:grid-cols-4">
        {facts.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="text-caption text-ink-2">{k}</dt>
            <dd className="truncate text-ui">{v || <span className="text-ink-3">None</span>}</dd>
          </div>
        ))}
      </dl>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="flex min-w-0 flex-col gap-4">
          {r.report ? (
            <>
              <StructuredCard report={r.report} />
              {r.conversation.length ? <ConversationCard r={r} /> : null}
            </>
          ) : (
            <RecordingCards r={r} />
          )}
          {r.triage ? <FixProposal t={r.triage} /> : null}
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <ResolutionCard r={r} companyId={companyId} />
          <Card title={`Diagnostics, ${events} ${events === 1 ? "event" : "events"}`}>
            <pre className="max-h-[480px] overflow-auto px-4 py-3 font-mono text-ink text-micro leading-relaxed">
              {JSON.stringify(r.diagnostics, null, 2)}
            </pre>
          </Card>
        </div>
      </div>
    </>
  );
}

function ResolutionCard({ r, companyId }: { r: BugReportDetail; companyId: string }) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<BugResolution | null>(null);
  const reopen = useMutation({
    mutationFn: () => ops.reopenReport(companyId, r.id),
    onSuccess: () => {
      toast("Reopened.");
      void qc.invalidateQueries({ queryKey: qk.report(companyId, r.id) });
      void qc.invalidateQueries({ queryKey: qk.allReports });
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const resolved = isResolved(r.status);
  return (
    <Card title="Status">
      <div className="flex flex-col gap-3 px-4 py-3 text-ui">
        {resolved ? (
          <>
            <p className="font-medium">{statusLabel(r.status, r.fixed_version)}</p>
            {r.resolution_note ? <p className="text-ink-2">{r.resolution_note}</p> : null}
            {r.resolved_at ? (
              <p className="text-caption text-ink-3">
                {r.resolved_by ? `${r.resolved_by.name}, ` : ""}
                {fmtDateTime(r.resolved_at)}
              </p>
            ) : null}
            <div>
              <Button size="sm" busy={reopen.isPending} onClick={() => reopen.mutate()}>
                Reopen
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" size="sm" onClick={() => setDialog("fixed")}>
              Mark fixed
            </Button>
            <Button size="sm" onClick={() => setDialog("wont_fix")}>
              Won't fix
            </Button>
            <Button size="sm" onClick={() => setDialog("not_a_bug")}>
              Not a bug
            </Button>
          </div>
        )}
        {r.triage_round > 0 || r.questions_asked > 0 ? (
          <p className="text-caption text-ink-3">
            {r.questions_asked} question{r.questions_asked === 1 ? "" : "s"} asked, {r.triage_round} triage
            run
            {r.triage_round === 1 ? "" : "s"}, ${r.triage_cost_usd.toFixed(2)}
          </p>
        ) : null}
      </div>
      {dialog ? (
        <ResolveDialog
          open
          onClose={() => setDialog(null)}
          companyId={companyId}
          reportIds={[r.id]}
          title={r.report?.title ?? null}
          initial={dialog}
        />
      ) : null}
    </Card>
  );
}

function RecordingCards({ r }: { r: BugReportDetail }) {
  return (
    <>
      {r.audio_url ? (
        <Card title="Recording">
          <div className="px-4 py-3">
            {/* biome-ignore lint/a11y/useMediaCaption: a spoken report; its transcript is below */}
            <audio controls preload="none" src={r.audio_url} className="w-full" />
          </div>
        </Card>
      ) : null}
      <Card title="Transcript">
        <div className="px-4 py-3 text-ui">
          {r.transcript ? (
            <p className="whitespace-pre-wrap">{r.transcript}</p>
          ) : r.transcript_status === "failed" ? (
            <p className="text-danger-ink">
              Not transcribed{r.transcript_error ? `: ${r.transcript_error}` : "."}
            </p>
          ) : r.transcript_status === "pending" ? (
            <p className="text-ink-3">Transcribing.</p>
          ) : (
            <p className="text-ink-3">No transcript.</p>
          )}
        </div>
      </Card>
      {r.note ? (
        <Card title="Note">
          <p className="whitespace-pre-wrap px-4 py-3 text-ui">{r.note}</p>
        </Card>
      ) : null}
    </>
  );
}

const FREQUENCY: Record<BugStructuredReport["frequency"], string> = {
  once: "Happened once",
  sometimes: "Happens sometimes",
  often: "Happens often",
  always: "Happens every time",
  unknown: "Frequency unknown",
};

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-caption text-ink-2">{label}</span>
      {children}
    </div>
  );
}

function StructuredCard({ report }: { report: BugStructuredReport }) {
  const kind = report.kind ?? "bug";
  return (
    <Card title="Report">
      <div className="flex flex-col gap-3 px-4 py-3 text-ui">
        {kind === "bug" ? (
          <div className="flex flex-wrap gap-1.5">
            <Badge tone="neutral">{report.blocking ? "Blocks work" : "Has a workaround"}</Badge>
            <Badge tone="neutral">{FREQUENCY[report.frequency]}</Badge>
          </div>
        ) : null}
        <p className="whitespace-pre-wrap">{report.summary}</p>
        {report.need ? (
          <Labeled label="The need">
            <p className="whitespace-pre-wrap">{report.need}</p>
          </Labeled>
        ) : null}
        {report.why ? (
          <Labeled label="Why it matters">
            <p className="whitespace-pre-wrap">{report.why}</p>
          </Labeled>
        ) : null}
        {report.steps.length > 0 ? (
          <Labeled label="Steps">
            <ol className="list-decimal pl-5">
              {report.steps.map((step, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: steps never reorder
                <li key={i}>{step}</li>
              ))}
            </ol>
          </Labeled>
        ) : null}
        {report.expected ? (
          <Labeled label="Expected">
            <p className="whitespace-pre-wrap">{report.expected}</p>
          </Labeled>
        ) : null}
        {report.actual ? (
          <Labeled label="What happened">
            <p className="whitespace-pre-wrap">{report.actual}</p>
          </Labeled>
        ) : null}
      </div>
    </Card>
  );
}

function ConversationCard({ r }: { r: BugReportDetail }) {
  const name = r.user?.name?.split(" ")[0] ?? "Rep";
  return (
    <Card title="Conversation">
      <div className="flex max-h-[480px] flex-col gap-2.5 overflow-y-auto px-4 py-3 text-ui">
        {r.conversation.map((t, i) =>
          t.role === "tool" ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: turns never reorder
            <p key={i} className="text-caption text-ink-3">
              Checked {t.tool_name ?? "a tool"}
            </p>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: turns never reorder
            <div key={i} className="flex flex-col gap-0.5">
              <span className="text-caption text-ink-2">
                {t.role === "user" ? name : "Assistant"}
                <span className="text-ink-3">
                  {" "}
                  {t.mode === "voice" ? "spoke" : "typed"}
                  {t.at ? `, ${fmtDateTime(t.at)}` : ""}
                </span>
              </span>
              {t.role === "user" ? (
                <p className="whitespace-pre-wrap">{t.text}</p>
              ) : (
                <Markdown text={t.text ?? ""} />
              )}
            </div>
          ),
        )}
      </div>
    </Card>
  );
}
